import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BadgeCheck, LogOut, Phone, Copy, Check, X, Undo2, Search, Loader2, Wallet, ArrowLeft, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { EkycChat } from "@/components/EkycChat";

const fmtTime = (iso) =>
  iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true }) : "";
const HOME_FOR = { member: "/home", team_staff: "/my-ledger", resend_staff: "/resend-only", delivery_man: "/delivery" };
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function EkycDesk() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const isStaff = ["admin", "ekyc_staff"].includes(user?.role);
  const views = isStaff ? [["desk", "Desk"], ["chat", "Chat"], ["hisab", "Mera Hisab"]] : [["chat", "Chat"], ["desk", "Records"]];
  const [view, setView] = useState(params.get("view") || (isStaff ? "desk" : "chat"));
  const backTo = user?.role === "admin" ? "/admin?tab=ekyc" : HOME_FOR[user?.role];
  const [settings, setSettings] = useState(null);
  const [groups, setGroups] = useState(null);

  const loadGroups = useCallback(() => {
    api.get("/ekyc/groups").then(({ data }) => setGroups(data.groups)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => {
    api.get("/ekyc/settings").then(({ data }) => setSettings(data)).catch((e) => toast.error(formatApiError(e)));
    loadGroups();
  }, [loadGroups]);

  return (
    <div className="min-h-screen bg-[#F8F8F3]" data-testid="ekyc-desk-page">
      <div className="bg-[#385C4B] text-[#F5F5F0] px-3 sm:px-6 py-3 flex items-center gap-2 sticky top-0 z-10">
        {backTo && (
          <button onClick={() => navigate(backTo)} className="p-1 rounded hover:bg-white/10" data-testid="ekyc-desk-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        <BadgeCheck className="w-5 h-5" />
        <h1 className="font-bold">{isStaff ? "EKYC Desk" : "EKYC Chat"}</h1>
        <span className="text-xs opacity-80 truncate hidden sm:inline">· {user?.display_name}</span>
        <div className="ml-auto flex items-center gap-1">
          {views.map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`ekyc-view-${k}`}
              className={`text-xs font-mono uppercase px-2.5 py-1.5 rounded ${view === k ? "bg-[#F5F5F0] text-[#385C4B]" : "hover:bg-white/10"}`}>
              {l}
            </button>
          ))}
          <button onClick={logout} className="p-1.5 rounded hover:bg-white/10" title="Logout" data-testid="ekyc-logout">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-3 sm:px-6 py-4">
        {!settings || groups === null ? (
          <Center><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</Center>
        ) : view === "chat" ? (
          <EkycChat initialGroupId={params.get("group") || ""} />
        ) : view === "hisab" && isStaff ? (
          <MyHisab />
        ) : groups.length === 0 ? (
          <Center testid="ekyc-no-groups">Aapko abhi koi EKYC group assign nahi hua. Admin se contact karo.</Center>
        ) : (
          <Desk groups={groups} settings={settings} onChanged={loadGroups} readOnly={!isStaff} />
        )}
      </div>
    </div>
  );
}

function Center({ children, testid }) {
  return <div className="py-10 text-center text-sm text-[#6B6B61]" data-testid={testid}>{children}</div>;
}

function Desk({ groups, settings, onChanged, readOnly }) {
  const companies = settings.companies.filter((c) => groups.some((g) => g.company === c));
  const [company, setCompany] = useState(companies[0]);
  const list = groups.filter((g) => g.company === company);
  const [groupId, setGroupId] = useState(list[0]?.id || "");
  useEffect(() => {
    if (!list.find((g) => g.id === groupId)) setGroupId(list[0]?.id || "");
  }, [company]); // eslint-disable-line react-hooks/exhaustive-deps
  const group = groups.find((g) => g.id === groupId);

  return (
    <div className="space-y-3">
      <div className="flex gap-2 overflow-x-auto" data-testid="ekyc-company-tabs">
        {companies.map((c) => {
          const pend = groups.filter((g) => g.company === c).reduce((a, g) => a + (g.counts?.pending || 0), 0);
          return (
            <button key={c} onClick={() => setCompany(c)} data-testid={`ekyc-company-${c}`}
              className={`px-4 py-2 rounded-md text-sm font-bold border ${company === c ? "bg-[#385C4B] text-white border-[#385C4B]" : "bg-white border-[#DCDCD3] text-[#385C4B]"}`}>
              {c} <span className="font-mono text-xs opacity-80">({pend})</span>
            </button>
          );
        })}
      </div>
      <select value={groupId} onChange={(e) => setGroupId(e.target.value)} data-testid="ekyc-group-select"
        className="w-full sm:w-auto px-3 py-2 text-sm border border-[#DCDCD3] rounded bg-white">
        {list.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
      </select>
      {group && (
        <GroupRecords key={group.id} group={group} checklist={settings.checklists[group.company] || []}
          failReasons={settings.fail_reasons} onChanged={onChanged} readOnly={readOnly} />
      )}
    </div>
  );
}

function GroupRecords({ group, checklist, failReasons, onChanged, readOnly }) {
  const [status, setStatus] = useState("pending");
  const [q, setQ] = useState("");
  const [records, setRecords] = useState(null);

  const load = useCallback(() => {
    api.get("/ekyc/records", { params: { group_id: group.id, status, q } })
      .then(({ data }) => setRecords(data.records))
      .catch((e) => toast.error(formatApiError(e)));
  }, [group.id, status, q]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const act = async (rec, path, body, msg) => {
    try {
      const { data } = await api.post(`/ekyc/records/${rec.id}/${path}`, body);
      if (path === "step") {
        setRecords((rs) => rs.map((r) => (r.id === rec.id ? data : r)));
        return;
      }
      setRecords((rs) => rs.filter((r) => r.id !== rec.id));
      toast.success(msg);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const c = group.counts || {};
  return (
    <>
      <div className="grid grid-cols-3 gap-2" data-testid="ekyc-kpis">
        {[["pending", "Pending", "text-amber-700"], ["done", "Done", "text-emerald-700"], ["failed", "Failed", "text-[#C8553D]"]].map(([k, l, cls]) => (
          <div key={k} className="bg-white border border-[#EDEDE5] rounded-lg p-3">
            <div className={`text-[10px] font-mono uppercase ${cls}`}>{l}</div>
            <div className={`text-2xl font-bold ${cls}`} data-testid={`ekyc-kpi-${k}`}>{c[k] || 0}</div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-[#EDEDE5] rounded-lg">
        <div className="flex flex-wrap items-center gap-2 p-2 border-b border-[#EDEDE5]">
          {["pending", "done", "failed"].map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`ekyc-status-${s}`}
              className={`text-xs font-mono uppercase px-3 py-1.5 rounded ${status === s ? "bg-[#385C4B] text-white" : "bg-[#F0F0EA] text-[#385C4B]"}`}>
              {s}
            </button>
          ))}
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-[#6B6B61]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search naam / mobile / ID…"
              data-testid="ekyc-search" className="w-full pl-7 pr-2 py-1.5 text-sm border border-[#DCDCD3] rounded" />
          </div>
        </div>
        {records === null ? (
          <Center><Loader2 className="w-4 h-4 animate-spin inline" /></Center>
        ) : records.length === 0 ? (
          <Center testid="ekyc-empty">Koi record nahi.</Center>
        ) : (
          <div className="divide-y divide-[#F0F0EA]">
            {records.map((r) => (
              <RecordRow key={r.id} rec={r} idLabel={group.id_label} checklist={checklist} failReasons={failReasons} act={act} readOnly={readOnly} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function RecordRow({ rec, idLabel, checklist, failReasons, act, readOnly }) {
  const [failing, setFailing] = useState(false);
  const [reason, setReason] = useState(failReasons[0]);
  const stepsOk = checklist.every((s) => rec.steps_done?.includes(s));
  const share = () => api.post(`/ekyc/chat/${rec.group_id}/messages`, { record_id: rec.id })
    .then(() => toast.success("Chat me share ho gaya")).catch((e) => toast.error(formatApiError(e)));
  const copy = () => navigator.clipboard.writeText(rec.consumer_id).then(() => toast.success(`Copied · ${rec.consumer_id}`)).catch(() => {});
  return (
    <div className="px-3 py-2.5 space-y-1.5" data-testid={`ekyc-row-${rec.consumer_id}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="font-bold text-[#1C1C19]">{rec.name || "—"}</span>
        {rec.mobile && (
          <a href={`tel:${rec.mobile}`} className="font-mono text-[#385C4B] flex items-center gap-1" data-testid={`ekyc-call-${rec.consumer_id}`}>
            <Phone className="w-3.5 h-3.5" />{rec.mobile}
          </a>
        )}
        <button onClick={copy} className="font-mono text-xs flex items-center gap-1 text-[#6B6B61] hover:text-[#385C4B]" data-testid={`ekyc-copy-${rec.consumer_id}`}>
          {idLabel} {rec.consumer_id} <Copy className="w-3 h-3" />
        </button>
        {rec.remark && <span className="text-xs text-amber-700">· {rec.remark}</span>}
        <button onClick={share} className="ml-auto text-[11px] flex items-center gap-1 text-[#2F6F8F] hover:underline" data-testid={`ekyc-share-${rec.consumer_id}`}>
          <Share2 className="w-3 h-3" />Share to chat
        </button>
      </div>
      {readOnly ? (
        <div className="flex flex-wrap items-center gap-2 text-xs text-[#6B6B61]" data-testid={`ekyc-readonly-${rec.consumer_id}`}>
          <span className={`font-bold ${rec.status === "done" ? "text-emerald-700" : rec.status === "failed" ? "text-[#C8553D]" : "text-amber-700"}`}>{rec.status.toUpperCase()}</span>
          {rec.fail_reason && <span>· {rec.fail_reason}</span>}
          {rec.done_at && <span>· {rec.done_by_name} · {fmtTime(rec.done_at)}</span>}
        </div>
      ) : rec.status === "pending" ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {checklist.map((s) => {
            const on = rec.steps_done?.includes(s);
            return (
              <button key={s} onClick={() => act(rec, "step", { step: s, checked: !on })} data-testid={`ekyc-step-${rec.consumer_id}-${s}`}
                className={`text-[11px] px-2 py-1 rounded-full border flex items-center gap-1 ${on ? "bg-emerald-50 border-emerald-500 text-emerald-700" : "border-[#DCDCD3] text-[#6B6B61]"}`}>
                {on && <Check className="w-3 h-3" />}{s}
              </button>
            );
          })}
          <div className="ml-auto flex items-center gap-1.5">
            {failing ? (
              <>
                <select value={reason} onChange={(e) => setReason(e.target.value)} className="text-xs border border-[#DCDCD3] rounded px-1 py-1" data-testid={`ekyc-fail-reason-${rec.consumer_id}`}>
                  {failReasons.map((f) => <option key={f}>{f}</option>)}
                </select>
                <button onClick={() => act(rec, "failed", { reason }, "Failed marked")} data-testid={`ekyc-fail-confirm-${rec.consumer_id}`}
                  className="text-xs px-2 py-1 rounded bg-[#C8553D] text-white">Save</button>
                <button onClick={() => setFailing(false)} className="text-xs px-1"><X className="w-3.5 h-3.5" /></button>
              </>
            ) : (
              <>
                <button onClick={() => setFailing(true)} data-testid={`ekyc-fail-${rec.consumer_id}`}
                  className="text-xs px-2.5 py-1.5 rounded border border-[#C8553D] text-[#C8553D] flex items-center gap-1"><X className="w-3.5 h-3.5" />Failed</button>
                <button onClick={() => act(rec, "done", {}, "KYC Done ✓")} disabled={!stepsOk} data-testid={`ekyc-done-${rec.consumer_id}`}
                  title={stepsOk ? "" : "Pehle saare steps tick karo"}
                  className="text-xs px-2.5 py-1.5 rounded bg-[#385C4B] text-white flex items-center gap-1 disabled:opacity-40"><Check className="w-3.5 h-3.5" />KYC Done</button>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2 text-xs text-[#6B6B61]">
          <span className={rec.status === "done" ? "text-emerald-700 font-bold" : "text-[#C8553D] font-bold"}>{rec.status.toUpperCase()}</span>
          {rec.fail_reason && <span>· {rec.fail_reason}</span>}
          <span>· {rec.done_by_name} · {fmtTime(rec.done_at)}</span>
          {rec.status === "done" && <span>· {inr(rec.rate_applied)}</span>}
          <button onClick={() => act(rec, "undo", {}, "Undo ho gaya")} data-testid={`ekyc-undo-${rec.consumer_id}`}
            className="ml-auto px-2 py-1 rounded border border-[#DCDCD3] flex items-center gap-1 hover:border-[#385C4B]"><Undo2 className="w-3 h-3" />Undo</button>
        </div>
      )}
    </div>
  );
}

export function LedgerView({ data }) {
  const cards = useMemo(() => [
    ["Aaj Done", data.today_done, "text-emerald-700"],
    ["Total Done", data.total_done, "text-[#385C4B]"],
    ["Earned", inr(data.earned), "text-blue-700"],
    ["Balance", inr(data.balance), "text-[#C8553D]"],
  ], [data]);
  return (
    <div className="space-y-3" data-testid="ekyc-ledger">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {cards.map(([l, v, cls]) => (
          <div key={l} className="bg-white border border-[#EDEDE5] rounded-lg p-3">
            <div className={`text-[10px] font-mono uppercase ${cls}`}>{l}</div>
            <div className={`text-xl font-bold ${cls}`} data-testid={`ekyc-ledger-${l.replace(/\s/g, "-").toLowerCase()}`}>{v}</div>
          </div>
        ))}
      </div>
      <Table title="Kaam (date-wise)" head={["Date", "Company", "Group", "Done", "Failed", "Rate", "Amount"]}
        rows={data.work.map((w) => [w.date, w.company, w.group_name, w.done, w.failed, inr(w.rate), inr(w.amount)])} />
      <Table title={`Payments · Paid ${inr(data.paid)}`} head={["Date", "Amount", "Mode", "Note"]}
        rows={data.payments.map((p) => [p.date, inr(p.amount), p.mode, p.note])} />
    </div>
  );
}

export function Table({ title, head, rows, testid }) {
  return (
    <div className="bg-white border border-[#EDEDE5] rounded-lg overflow-x-auto" data-testid={testid}>
      {title && <div className="px-3 py-2 border-b border-[#EDEDE5] text-[10px] font-mono uppercase tracking-[0.2em] text-[#385C4B]">{title}</div>}
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[10px] uppercase font-mono text-[#6B6B61]">{head.map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={head.length} className="px-3 py-4 text-center text-[#6B6B61]">Koi entry nahi</td></tr>
          ) : rows.map((r, i) => (
            <tr key={i} className="border-t border-[#F0F0EA]">{r.map((c, j) => <td key={j} className="px-3 py-2 whitespace-nowrap">{c}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MyHisab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/ekyc/ledger/me").then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  if (!data) return <Center><Wallet className="w-5 h-5 inline mr-2" />Loading…</Center>;
  return <LedgerView data={data} />;
}
